import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import axios from "utils/axios";
import configFile from "config.json";
import { Button, Form, Input, InputNumber, Modal, Select, Switch } from "antd";
import { PlusOutlined, EyeOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { NotificationManager } from "react-notifications";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const { TextArea } = Input;

const labelWithRequired = (text) => (
  <span>
    {text}
    <Required>*</Required>
  </span>
);

const resolveIsActive = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === null) {
    return false;
  }
  return true;
};

const resolveTagId = (tag, tagOptions = []) => {
  if (tag === null || tag === undefined) {
    return null;
  }

  if (typeof tag === "number") {
    return tag;
  }

  if (typeof tag === "string") {
    const trimmed = tag.trim();
    if (!trimmed) {
      return null;
    }

    const tagById = tagOptions.find((opt) => String(opt?.id) === trimmed);
    if (tagById?.id !== undefined && tagById?.id !== null) {
      return tagById.id;
    }

    const normalized = trimmed.toLowerCase();
    const tagByLabel = tagOptions.find((opt) => {
      const labels = [opt?.slug, opt?.name, opt?.label, opt?.title, opt?.value]
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
        .map((value) => String(value).trim().toLowerCase());
      return labels.includes(normalized);
    });

    return tagByLabel?.id ?? trimmed;
  }

  if (typeof tag === "object") {
    if (tag?.id !== undefined && tag?.id !== null) {
      return tag.id;
    }
    if (tag?.value !== undefined && tag?.value !== null) {
      return tag.value;
    }
    if (tag?.key !== undefined && tag?.key !== null) {
      return tag.key;
    }
    return resolveTagId(tag?.slug || tag?.name || tag?.label || tag?.title, tagOptions);
  }

  return null;
};

const resolveImageUrl = (image) => {
  if (!image) {
    return "";
  }
  if (typeof image === "string") {
    return image.trim();
  }
  if (typeof image !== "object") {
    return "";
  }

  const candidate = image.url || image.imageUrl || image.src || image.path;
  if (candidate === null || candidate === undefined) {
    return "";
  }
  return String(candidate).trim();
};

const EMPTY_TAG_IDS = [];

const BasicTab = ({ deal = {}, tagOptions = [], disabled, form, onSaved, availableImages = [] }) => {
  const [selectedImage, setSelectedImage] = useState(deal?.imageUrl ? { url: deal.imageUrl } : null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [search, setSearch] = useState("");
  const dealId = deal?.id;
  const selectedImageUrl = useMemo(() => resolveImageUrl(selectedImage), [selectedImage]);
  const normalizedSelectedImage = useMemo(() => {
    if (!selectedImageUrl) {
      return null;
    }
    if (selectedImage && typeof selectedImage === "object") {
      return { ...selectedImage, url: selectedImageUrl };
    }
    return { url: selectedImageUrl };
  }, [selectedImage, selectedImageUrl]);

  const normalizedDealTags = useMemo(() => {
    if (!Array.isArray(deal?.tags) || deal.tags.length === 0) {
      return EMPTY_TAG_IDS;
    }
    const resolved = deal.tags
      .map((tag) => resolveTagId(tag, tagOptions))
      .filter((tagId) => tagId !== null && tagId !== undefined && tagId !== "");
    return resolved.length > 0 ? resolved : EMPTY_TAG_IDS;
  }, [deal?.tags, tagOptions]);

  useEffect(() => {
    if (!form) {
      return;
    }
    form.setFieldsValue({
      name: deal.name || "",
      masterName: deal.masterName || "",
      defaultPrice: deal.defaultPrice ?? null,
      pickupPrice: deal.pickupPrice ?? null,
      comboMenuId: deal.comboMenuId || "",
      tags: normalizedDealTags,
      description: deal.description || "",
      isActive: resolveIsActive(deal.isActive),
    });
    setSelectedImage(deal?.imageUrl ? { url: deal.imageUrl } : null);
  }, [deal, form, normalizedDealTags]);

  const filteredImages = availableImages.filter((img) =>
    (img.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  const closePickerModal = () => {
    setPickerOpen(false);
    setSearch("");
  };

  const assignTag = useCallback(async (tagId, comboId) => {
    try {
      const { data: result } = await axios.post(`${configFile.PROD_SERVER_URL}/menu2/manageCombo/assignTagCombo`, {
        comboId,
        tagId
      });
      console.log(result);
    } catch (err) {
      NotificationManager.error(
        err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
        "Error",
        5000
      );
      console.log(err);
    }
  }, []);

  const unassignTag = useCallback(async (tagId, comboId) => {
    try {
      const { data: result } = await axios.post(`${configFile.PROD_SERVER_URL}/menu2/manageCombo/unassignTagCombo`,
        {
          comboId,
          tagId
        }
      );
      console.log(result);
    } catch (err) {
      NotificationManager.error(
        err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
        "Error",
        5000
      );
      console.log(err);
    }
  }, []);

  const resolveSelectTagId = useCallback((value, option) => {
    const fromValue = resolveTagId(value, tagOptions);
    if (fromValue !== null && fromValue !== undefined && fromValue !== "") {
      return fromValue;
    }

    const optionValue = option?.value ?? option?.key;
    return resolveTagId(optionValue, tagOptions);
  }, [tagOptions]);

  const normalizeTagIds = useCallback((tags) => {
    if (!Array.isArray(tags)) {
      return [];
    }
    return [...new Set(tags
      .map((tag) => resolveTagId(tag, tagOptions))
      .filter((tagId) => tagId !== null && tagId !== undefined && tagId !== ""))];
  }, [tagOptions]);

  const submitComboBasicInfo = useCallback(async (values) => {
    try {
      const normalizedTagIds = normalizeTagIds(values?.tags);
      const payload = {
        name: values.name || "",
        masterName: values.masterName || "",
        defaultPrice: values.defaultPrice ?? null,
        pickupPrice: values.pickupPrice ?? null,
        comboMenuId: values.comboMenuId || "",
        description: values.description || "",
        isActive: resolveIsActive(values.isActive),
        imageUrl: new URL(selectedImageUrl) || null,
      };

      if (deal?.id) {
        await axios.post(`${API_BASE}/manageCombo/updateCombo`, {
          ...payload,
          id: deal.id,
        });
      } else {
        const { data: result } = await axios.post(`${API_BASE}/manageCombo/createCombo`, payload);
        const createdComboId = result?.data?.id ?? result?.data?.comboId ?? result?.id ?? result?.comboId;
        if (createdComboId && normalizedTagIds.length > 0) {
          await Promise.all(
            normalizedTagIds.map((tagId) =>
              axios.post(`${configFile.PROD_SERVER_URL}/menu2/manageCombo/assignTagCombo`, {
                comboId: createdComboId,
                tagId,
              })
            )
          );
        }
      }
      NotificationManager.success('Амжилттай');
    } catch (err) {
      console.log(err);
      NotificationManager.error(
        err?.response?.data?.errorMessage || err?.message || 'Комбо бүртгэхэд алдаа гарлаа',
        'Алдаа'
      );
      return;
    }
    if (deal?.id) {
      if (typeof onSaved === "function") {
        onSaved();
      }
      return;
    }
    window.location = '/deals';
  }, [deal, normalizeTagIds, onSaved, selectedImageUrl]);

  const handleTagSelect = useCallback((value, option) => {
    if (!dealId) {
      return;
    }
    const tagId = resolveSelectTagId(value, option);
    if (tagId === null || tagId === undefined || tagId === "") {
      return;
    }
    assignTag(tagId, dealId);
  }, [assignTag, dealId, resolveSelectTagId]);

  const handleTagDeselect = useCallback((value, option) => {
    if (!dealId) {
      return;
    }
    const tagId = resolveSelectTagId(value, option);
    if (tagId === null || tagId === undefined || tagId === "") {
      return;
    }
    unassignTag(tagId, dealId);
  }, [dealId, resolveSelectTagId, unassignTag]);

  const tagSelectOptions = useMemo(
    () =>
      tagOptions
        .map((tag) => {
          const value = tag?.id ?? tag?.value ?? tag?.slug;
          if (value === null || value === undefined || value === "") {
            return null;
          }
          return {
            value,
            label: tag?.slug || tag?.name || tag?.label || tag?.title || String(value),
          };
        })
        .filter(Boolean),
    [tagOptions]
  );

  return (
    <Card>
      <ImageSection>
        <ImageSectionTitle>Зураг</ImageSectionTitle>
        {selectedImageUrl ? (
          <SelectedImageWrap>
            <img
              src={selectedImageUrl}
              alt="combo"
              onClick={() => setPreviewImage(normalizedSelectedImage)}
            />
            <ImageActions>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setPreviewImage(normalizedSelectedImage)}
              />
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={disabled}
                onClick={() => setPickerOpen(true)}
              />
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={disabled}
                onClick={() => setSelectedImage(null)}
              />
            </ImageActions>
          </SelectedImageWrap>
        ) : (
          <SelectButton onClick={() => !disabled && setPickerOpen(true)} $disabled={disabled}>
            <PlusOutlined />
            <span>Зураг сонгох</span>
          </SelectButton>
        )}
      </ImageSection>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          console.log("BasicTab submit payload:", values);
          submitComboBasicInfo(values)
        }}
      >

        <FormGrid>
          <Form.Item label={labelWithRequired("Мастер нэр")} name="masterName">
            <Input disabled={disabled} />
          </Form.Item>
          <Form.Item label="Нэр" name="name">
            <Input disabled={disabled} />
          </Form.Item>

          <Form.Item  
            style={{ gridColumn: "1 / -1" }}
            label={labelWithRequired("SIMPHONY POS ID")} 
            name="comboMenuId"
          >
            <Input disabled={disabled} />
          </Form.Item>
          <Form.Item label={labelWithRequired("Хүргэлтийн үнэ")} name="defaultPrice">
            <InputNumber disabled={disabled} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Очиж авах үнэ" name="pickupPrice">
            <InputNumber disabled={disabled} style={{ width: "100%" }} />
          </Form.Item>


          <Form.Item label="Tag" name="tags">
            <Select
              mode="multiple"
              options={tagSelectOptions}
              onSelect={handleTagSelect}
              onDeselect={handleTagDeselect}
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item label="Идэвхтэй" name="isActive" valuePropName="checked">
            <Switch disabled={disabled} />
          </Form.Item>


          <Form.Item
            label={labelWithRequired("Комбо тайлбар")}
            style={{ gridColumn: "1 / -1" }}
            name="description"
          >
            <TextArea rows={4} disabled={disabled} />
          </Form.Item>

          <Form.Item style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" onClick={() => form?.submit?.()} disabled={disabled}>
              Хадгалах
            </Button>
          </Form.Item>
        </FormGrid>
      </Form>

      {/* Image Picker Modal */}
      <Modal
        open={pickerOpen}
        title="Зураг сонгох"
        footer={null}
        onCancel={closePickerModal}
        width={800}
      >
        <Input
          placeholder="Нэрээр хайх"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ marginBottom: 12 }}
        />
        {filteredImages.length === 0 ? (
          <EmptyImages>Зураг олдсонгүй</EmptyImages>
        ) : (
          <PickerGrid>
            {filteredImages.map((img) => {
              const imageUrl = resolveImageUrl(img);
              if (!imageUrl) {
                return null;
              }
              return (
                <PickerItem
                  key={img?.id || imageUrl}
                  onClick={() => {
                    setSelectedImage(img);
                    closePickerModal();
                  }}
                >
                  <img src={imageUrl} alt={img?.name || img?.id || "image"} />
                  {img?.name && <span>{img.name}</span>}
                </PickerItem>
              );
            })}
          </PickerGrid>
        )}
      </Modal>

      {/* Preview Modal */}
      <Modal
        open={previewImage !== null}
        title={previewImage?.name || "Preview"}
        footer={null}
        onCancel={() => setPreviewImage(null)}
        centered
      >
        {previewImage && (
          <img src={previewImage.url} alt={previewImage.name} style={{ width: "100%" }} />
        )}
      </Modal>
    </Card>
  )
};

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 22px 24px 28px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(22, 119, 255, 0.16);
  backdrop-filter: blur(8px);
`;

const ImageSection = styled.div`
  margin-bottom: 24px;
`;

const ImageSectionTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #3b4b5a;
  letter-spacing: 0.02em;
`;

const SelectedImageWrap = styled.div`
  img {
    width: 100%;
    max-height: 220px;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    display: block;
  }
`;

const ImageActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const SelectButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${({ $disabled }) => ($disabled ? "#b0b8c1" : "#667085")};
  border: 1px dashed ${({ $disabled }) => ($disabled ? "#e0e4ea" : "#d0d5dd")};
  border-radius: 6px;
  padding: 40px 20px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? "#e0e4ea" : "#2563eb")};
    color: ${({ $disabled }) => ($disabled ? "#b0b8c1" : "#2563eb")};
  }
`;

const PickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  max-height: 460px;
  overflow-y: auto;
  padding: 4px;
`;

const PickerItem = styled.div`
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.2s;
  text-align: center;

  img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    display: block;
  }

  span {
    font-size: 11px;
    padding: 4px;
    display: block;
    color: #667085;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: #2563eb;
  }
`;

const EmptyImages = styled.div`
  text-align: center;
  color: #667085;
  padding: 32px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(240px, 1fr);
  gap: 18px 24px;

  .ant-input,
  .ant-input-number,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-input-number-input,
  .ant-input-number-affix-wrapper {
    border-radius: 6px !important;
    border-color: rgba(22, 119, 255, 0.2) !important;
    box-shadow: inset 0 0 0 1px rgba(22, 119, 255, 0.03);
  }

  .ant-select-selector {
    padding: 4px 12px !important;
  }

  .ant-form-item-label > label {
    color: #3b4b5a;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Required = styled.span`
  color: #e05858;
  margin-left: 4px;
`;

export default BasicTab;
